import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Bed,
  Package,
  Users,
  BarChart3,
  Calendar,
  ImageIcon,
  Settings,
  LogOut,
  Menu,
  X,
  Hotel,
  CalendarPlus,
  BookOpen,
  Globe,
  Ticket,
  UserCog,
  ClipboardList,
  Archive,
  FileText,
  Tag,
} from 'lucide-react';

// roles: omit to show to all authenticated users
const NAV_ITEMS = [
  { name: 'Dashboard',      href: '/dashboard',     icon: LayoutDashboard, roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Bookings',       href: '/bookings',      icon: BookOpen,        roles: ['HotelHead', 'HotelAdmin', 'HotelStaff', 'Admin'] },
  { name: 'Tickets',        href: '/tickets',       icon: Ticket,          roles: ['HotelHead', 'HotelAdmin', 'HotelStaff', 'Admin'] },
  { name: 'Admin Booking',  href: '/admin-booking', icon: CalendarPlus,    roles: ['HotelHead', 'HotelAdmin', 'HotelStaff', 'Admin'] },
  { name: 'Calendar',       href: '/calendar',      icon: Calendar,        roles: ['HotelHead', 'HotelAdmin', 'HotelStaff', 'Admin'] },
  { name: 'Rooms',          href: '/rooms',         icon: Bed,             roles: ['HotelHead', 'HotelAdmin', 'HotelStaff', 'Admin'] },
  { name: 'Packages',       href: '/packages',      icon: Package,         roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Gallery',        href: '/gallery',       icon: ImageIcon,       roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Explore Page',   href: '/explore',       icon: Globe,           roles: ['HotelHead', 'Admin'] },
  { name: 'Portal Accounts',href: '/staff',         icon: UserCog,         roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Inventory',      href: '/inventory',     icon: Archive,         roles: ['HotelHead', 'HotelAdmin', 'HotelStaff', 'Admin'] },
  { name: 'HR',             href: '/hr',            icon: ClipboardList,   roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Reports',        href: '/reports',       icon: BarChart3,       roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'About Content',  href: '/about-content', icon: FileText,        roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Coupons',        href: '/coupons',       icon: Tag,             roles: ['HotelHead', 'HotelAdmin', 'Admin'] },
  { name: 'Settings',       href: '/settings',      icon: Settings,        roles: ['HotelHead', 'Admin'] },
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { logout, user } = useAuth();

  const visibleItems = NAV_ITEMS.filter(item =>
    !item.roles || item.roles.includes(user?.role)
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center space-x-3 p-6 border-b border-slate-700">
        <div className="p-2 bg-blue-600 rounded-lg">
          <Hotel className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-base font-bold text-white truncate">{user?.hotelName || 'Hotel Admin'}</h1>
          <p className="text-xs text-slate-400">Management Portal</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`
              }
              onClick={() => setIsOpen(false)}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Role badge + Logout */}
      <div className="p-4 border-t border-slate-700 space-y-3">
        {user && (
          <div className="px-4 py-2 bg-slate-700/40 rounded-lg">
            <p className="text-xs text-slate-500 mb-0.5">Logged in as</p>
            <p className="text-white text-sm font-medium truncate">{user.fullName || user.userName}</p>
            <p className="text-slate-400 text-xs">{user.role}</p>
          </div>
        )}
        <Button
          onClick={logout}
          variant="ghost"
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-red-600/20 transition-all duration-200"
        >
          <LogOut className="h-5 w-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden fixed top-4 left-4 z-50 bg-slate-800/80 backdrop-blur-sm border border-slate-700"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="h-5 w-5 text-white" /> : <Menu className="h-5 w-5 text-white" />}
      </Button>

      {/* Mobile overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setIsOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-800 border-r border-slate-700 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
